import { useCallback, useEffect, useMemo, useState } from "react";

import { useAdmin } from "../../../hooks/useAdmin";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import { useListTripReservation } from "./useListTripReservation";
import { buildTripReservationsListVariables } from "../utils/tripReservationListParams";

const normalizePage = (page) => Math.max(1, Number(page) || 1);

/**
 * Liste de /admin/reservations : pagination, recherche, statut et établissement
 * côté serveur. Même logique que /admin/circuits (useManageTripPublication) :
 * recherche debouncée partagée (useDebouncedSearch), filtre établissement
 * alimenté par la source mutualisée de useAdmin, chaque changement de filtre
 * revient à la page 1.
 */
export const useManageTripReservation = () => {
  const { organization, establishmentsPermission, accessToken, manageableEstablishments, assignableEstablishments } =
    useAdmin();

  const [currentPage, setCurrentPageState] = useState(1);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilterState] = useState("all");
  const [selectedEstablishment, setSelectedEstablishmentState] = useState("all");

  const setCurrentPage = useCallback((page) => {
    setCurrentPageState(normalizePage(page));
  }, []);

  const setStatusFilter = useCallback((value) => {
    setStatusFilterState(value);
    setCurrentPageState(1);
  }, []);

  const setSelectedEstablishment = useCallback((value) => {
    setSelectedEstablishmentState(value);
    setCurrentPageState(1);
  }, []);

  // Le terme n'est appliqué (debouncedSearch) qu'après le délai de
  // useDebouncedSearch. La page ne repart à 1 que si le terme appliqué
  // change vraiment (une frappe sous le seuil ne touche pas la page).
  useEffect(() => {
    setCurrentPageState(1);
  }, [debouncedSearch]);

  const canQuery = Boolean(organization && (establishmentsPermission || []).length > 0);

  // Même liste que les filtres « utilisateurs » et « circuits » : valides
  // (ACTIVATED) et administrables. Source commune : useAdmin. Aucun appel API
  // dédié pour les établissements.
  const establishmentFilterOptions = useMemo(
    () => [
      {
        index: 0,
        value: "all",
        text: "Tous les établissements",
      },
      ...assignableEstablishments.map((item, index) => ({
        index: index + 1,
        value: item.id,
        text: item.company?.legalName,
      })),
    ],
    [assignableEstablishments]
  );

  const selectedEstablishmentIds = useMemo(() => {
    if (selectedEstablishment === "all") {
      return manageableEstablishments.map((item) => item.id);
    }
    return [selectedEstablishment];
  }, [selectedEstablishment, manageableEstablishments]);

  const listVariables = useMemo(
    () =>
      buildTripReservationsListVariables({
        currentPage: Math.max(0, currentPage - 1),
        search: debouncedSearch,
        status: statusFilter === "all" ? undefined : statusFilter,
        establishmentIds: selectedEstablishmentIds,
      }),
    [currentPage, debouncedSearch, statusFilter, selectedEstablishmentIds]
  );

  const { tripReservationsData, freshData, loading, error, refetch } = useListTripReservation(
    listVariables,
    accessToken,
    !canQuery
  );

  const reservations = useMemo(
    () => tripReservationsData?.content || [],
    [tripReservationsData]
  );

  return {
    reservations,
    // Premier chargement : aucune liste à afficher encore.
    reservationsLoading: loading && !tripReservationsData,
    // Nouveau filtre ou page : la liste précédente reste affichée, grisée.
    reservationsRefreshing: loading && Boolean(tripReservationsData),
    error,
    currentPage,
    setCurrentPage,
    nbPages: (freshData ?? tripReservationsData)?.totalPages || 0,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedEstablishment,
    setSelectedEstablishment,
    establishmentFilterOptions,
    refetch,
  };
};
